/**
 * BidiText — inline wrapper that gives a piece of text its own base direction
 * (a bidi isolate), so mixed Hebrew/English text lays out logically regardless
 * of the surrounding document direction.
 *
 * Use it for content whose language is genuinely UNKNOWN at author time —
 * user-generated or tenant-authored: entity, product and person names, workspace
 * and board names, agent names, search results, menu items naming a record.
 *
 * **Do NOT use it for our own translated copy.** A `t()` string is written in the
 * UI locale, so its base direction is the document's and there is nothing to
 * detect; guessing at it is what put "PanLuma" on the wrong side of
 * "PanLuma הוא מקור האמת". See STYLE_GUIDE § Bidirectional Text.
 *
 * The direction comes from {@link resolveBaseDirection} — a word-majority
 * heuristic, not the browser's `dir="auto"` first-strong rule, because a name
 * beginning with a Latin token ("SKU-99 מסנן שמן") is common and first-strong
 * flips the whole phrase on it. Where the children are not plain text (embedded
 * markup, a `<Trans>` placeholder) there is no string to count, so the span
 * degrades to native `dir="auto"`.
 *
 * It is INLINE and only affects the text run's own bidi ordering — it never
 * reorders the surrounding flex layout (icons, badges keep following the menu's
 * direction), so it is safe to drop into existing label spans.
 *
 * Usage:
 *   <BidiText className="truncate">{workspace.name}</BidiText>
 */

import React from 'react';
import { autoDirection, documentDirection, type BaseDirection } from '../../utils/bidi';

type BidiTextProps = React.HTMLAttributes<HTMLSpanElement> & {
  // Optional so the span can serve as a `<Trans components={{ n: <BidiText /> }}>`
  // placeholder — react-i18next injects the children via cloneElement at runtime.
  children?: React.ReactNode;
};

/**
 * The base direction `content` should be laid out in, for an element that
 * carries its own `dir` attribute and so cannot delegate to `<BidiText>` — a
 * heading, a breadcrumb crumb, a table cell.
 *
 * Returns `'auto'` when the content is not plain text, which is the correct
 * attribute value to hand the browser in that case.
 *
 * Named as a hook because that is how its call sites read, and because the
 * locale fallback may move back behind context later; today it reads
 * `<html dir>` directly, so a site inside a `.map()` or behind a `&&` can call
 * the plain `autoDirection(content, documentDirection())` instead.
 */
export function useAutoDirection(content: React.ReactNode): BaseDirection | 'auto' {
  return autoDirection(content, documentDirection());
}

// `forwardRef` so the label can be used directly as a Radix `asChild` Slot
// child (e.g. a `<TooltipTrigger asChild>` / `<PopoverTrigger asChild>`)
// without React warning that a function component cannot receive a ref.
export const BidiText = React.forwardRef<HTMLSpanElement, BidiTextProps>(
  function BidiText({ children, ...props }, ref) {
    const dir = useAutoDirection(children);
    return (
      // An explicit `dir` makes the element a bidi isolate exactly as `auto`
      // does (the UA stylesheet sets `unicode-bidi: isolate` for both), so the
      // text is still shielded from the surrounding direction either way.
      <span dir={dir} ref={ref} {...props}>
        {children}
      </span>
    );
  }
);
